import React from "react";
import { Check, X, XCircle } from "lucide-react";
import type { ActionType, Objection } from "./types";

interface ObjectionDetailModalProps {
  objection: Objection;
  onClose: () => void;
  onAction: (objection: Objection, actionType: ActionType) => void;
}

const DetailLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </label>
);

const getStatusClassName = (status: Objection["status"]) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "pending":
      return "bg-yellow-100 text-yellow-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

export const ObjectionDetailModal: React.FC<ObjectionDetailModalProps> = ({
  objection,
  onClose,
  onAction,
}) => {
  const isNonEmptyObject = (obj: any) => {
    if (Object.keys(obj).length > 0) {
      return obj;
    } else return null;
  };
  const highcourtInfo = isNonEmptyObject(objection.highcourtInfo);
  const districtCourtInfo = isNonEmptyObject(objection.districtCourtInfo);
  const isApproved = objection.status === "approved";
  const isRejected = objection.status === "rejected";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Objection Details
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              CNR: {objection.cnr || "N/A"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
            aria-label="Close"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          <div className="space-y-6">
            <section>
              <h4 className="mb-4 text-sm font-bold uppercase tracking-wide text-blue-900">
                Objection Information
              </h4>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <DetailLabel>Title</DetailLabel>
                  <p className="break-words text-sm font-semibold text-gray-900">
                    {objection.objectionTitle || "N/A"}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <DetailLabel>Description</DetailLabel>
                  <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-700">
                    {objection.description || "N/A"}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <DetailLabel>Legal Ground</DetailLabel>
                  <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-700">
                    {objection.legalGround || "N/A"}
                  </p>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-5">
              <h4 className="mb-4 text-sm font-bold uppercase tracking-wide text-blue-900">
                Status & Hearing
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg bg-gray-50 p-4">
                  <DetailLabel>Objection Type</DetailLabel>
                  <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold capitalize text-blue-800">
                    {objection.objectionType || "N/A"}
                  </span>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <DetailLabel>Priority Level</DetailLabel>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                      objection.priorityLevel === "high" ||
                      objection.priorityLevel === "urgent"
                        ? "bg-red-100 text-red-700"
                        : objection.priorityLevel === "medium"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                    }`}
                  >
                    {objection.priorityLevel || "N/A"}
                  </span>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <DetailLabel>Status</DetailLabel>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${getStatusClassName(
                      objection.status,
                    )}`}
                  >
                    {objection.status || "N/A"}
                  </span>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <DetailLabel>CNR</DetailLabel>
                  <p className="break-all text-sm font-semibold text-gray-900">
                    {objection.cnr || "N/A"}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 p-4">
                  <DetailLabel>Hearing Date</DetailLabel>
                  <p className="text-sm font-semibold text-gray-900">
                    {objection.nextHearingDate
                      ? new Date(objection.nextHearingDate).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )
                      : "N/A"}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <DetailLabel>Filed Date</DetailLabel>
                  <p className="text-sm font-semibold text-gray-900">
                    {objection.createdAt
                      ? new Date(objection.createdAt).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )
                      : "N/A"}
                  </p>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-5">
              <h4 className="mb-4 text-sm font-bold uppercase tracking-wide text-blue-900">
                Court Information
              </h4>
              <div className="rounded-xl bg-gray-50 p-4">
                {districtCourtInfo && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <DetailLabel>Room Number</DetailLabel>
                      <p className="text-sm text-gray-700">
                        {districtCourtInfo?.roomNumber || "N/A"}
                      </p>
                    </div>
                    <div>
                      <DetailLabel>Court</DetailLabel>
                      <p className="text-sm font-semibold text-gray-900">
                        {districtCourtInfo?.courtRoomName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <DetailLabel>Complex</DetailLabel>
                      <p className="text-sm text-gray-700">
                        {districtCourtInfo?.complexName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <DetailLabel>District</DetailLabel>
                      <p className="text-sm text-gray-700">
                        {districtCourtInfo?.districtName || "N/A"}
                      </p>
                    </div>
                  </div>
                )}

                {highcourtInfo && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <DetailLabel>Room Number</DetailLabel>
                      <p className="text-sm text-gray-700">
                        {highcourtInfo?.courtRoom || "N/A"}
                      </p>
                    </div>
                    <div>
                      <DetailLabel>Bench Name</DetailLabel>
                      <p className="text-sm font-semibold text-gray-900">
                        {highcourtInfo.benchName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <DetailLabel>Complex</DetailLabel>
                      <p className="text-sm text-gray-700">
                        {highcourtInfo.bench || "N/A"}
                      </p>
                    </div>
                    <div>
                      <DetailLabel>High Court</DetailLabel>
                      <p className="text-sm text-gray-700">
                        {highcourtInfo.highCourtName || "N/A"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="border-t border-gray-200 pt-5">
              <h4 className="mb-4 text-sm font-bold uppercase tracking-wide text-blue-900">
                Advocate Information
              </h4>
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <DetailLabel>Name</DetailLabel>
                    <p className="text-sm font-semibold text-gray-900">
                      {objection.advocateDetails?.name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <DetailLabel>Username</DetailLabel>
                    <p className="text-sm text-gray-700">
                      {objection.advocateDetails?.username || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {objection.attachments?.length > 0 && (
              <section className="border-t border-gray-200 pt-5">
                <h4 className="mb-4 text-sm font-bold uppercase tracking-wide text-blue-900">
                  Attachments
                </h4>
                <div className="space-y-2">
                  {objection.attachments.map((attachment, index) => (
                    <div
                      key={`${attachment}-${index}`}
                      className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                    >
                      <span className="break-all text-sm text-gray-700">
                        {attachment}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row">
          <button
            type="button"
            disabled={isApproved}
            onClick={() => onAction(objection, "approve")}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
              isApproved
                ? "cursor-not-allowed bg-green-300 text-white"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            <Check className="h-4 w-4" />
            {isApproved ? "Approved" : "Approve"}
          </button>
          <button
            type="button"
            disabled={isRejected}
            onClick={() => onAction(objection, "reject")}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
              isRejected
                ? "cursor-not-allowed bg-red-300 text-white"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            <X className="h-4 w-4" />
            {isRejected ? "Rejected" : "Reject"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-lg bg-blue-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
